/**
 * Who wrote a row, and what the interface is allowed to claim about it.
 *
 * Every actor column the portal renders is nullable and `on delete set null`:
 * `request_comments.author_user_id`, `request_status_events.actor_user_id` and
 * `chat_messages.sender_user_id`. A person's Auth user can be removed by the lifecycle and their
 * rows survive it with a null actor, so null is not a shape only tests can produce. It is reachable
 * in production data at all three surfaces.
 *
 * All three surfaces used to compare the actor to the session's own user id and treat "not equal" as
 * the other side. Null is not equal to anything, so a null actor was rendered as the other side with
 * complete confidence: the client's request thread attributed a deleted colleague's comment to
 * **Petrovic**, the admin thread attributed the same absence to **Kunden**, and the chat placed the
 * message in Petrovic's position with no label at all to contradict it.
 *
 * The rule this file exists to hold, and the reason it is one file rather than three copies of a
 * ternary: **a null actor is never rendered as a person or as a side. It is rendered as unknown.**
 * Showing the wrong sender with confidence is worse than showing "unknown", because a reader has no
 * way to doubt a confident label and every way to doubt an honest one.
 *
 * ## The wording
 *
 * `Okänd avsändare` is a category in the same way `Ni`, `Kunden` and `Petrovic` are categories at the
 * sites where those are correct, so it cannot be misread as somebody's actual name. `Okänd` claims
 * only that the portal cannot tell, which is the whole of what a null column supports: it does not
 * say the account was deleted, it does not say the row is old, and it does not say which side the
 * writer was on. `avsändare` is a role in a conversation rather than a status of a person, so the
 * label describes the message and not the human who is missing from it.
 *
 * The same two words are used at all three surfaces. One absence should not have three names, and a
 * reader who meets it in the chat and then in a request thread should not have to work out whether
 * they are looking at the same thing.
 */

/** The one word for "the row does not say who". Identical in the client view, the admin view and the chat. */
export const UNKNOWN_ACTOR = "Okänd avsändare";

/** The avatar glyph that goes with it, which is what `initials` already renders for no name. */
export const UNKNOWN_ACTOR_INITIAL = "?";

/**
 * The three answers an actor column can support.
 *
 * `counterpart` means only "a known account that is not mine". It does **not** mean the other side
 * of the conversation: what the account belongs to is a separate question, and each surface answers
 * it from what its own read model actually carries.
 */
export type ActorSide = "self" | "counterpart" | "unknown";

export function actorSide(
  actorUserId: string | null | undefined,
  currentUserId: string | null | undefined
): ActorSide {
  // Null first, and before any comparison is attempted. `null === null` is true, so an actor column
  // that says nothing would otherwise be read as "self" whenever the reader's own id is also absent.
  if (actorUserId === null || actorUserId === undefined) {
    return "unknown";
  }

  // Not reachable from any current caller, because every surface renders its thread only after the
  // identity query has resolved. It is still the answer the rule requires: "not equal to my id" is
  // not a fact about a row when my own id is not known.
  if (currentUserId === null || currentUserId === undefined) {
    return "unknown";
  }

  return actorUserId === currentUserId ? "self" : "counterpart";
}

/**
 * Up to two initials for an avatar, or `?` when there is no name to take them from.
 *
 * Lives here rather than in one route because the chat and the request thread both draw the same
 * avatar, and a second copy of this would be a second place for the no-name case to drift.
 */
export function initials(value: string | null): string {
  if (!value) {
    return UNKNOWN_ACTOR_INITIAL;
  }

  return value
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join("");
}

/**
 * The supplier's one name, on every client surface.
 *
 * Petrovic is named as a company and never as an individual. Which person on the supplier's side
 * typed a reply is not a fact the client's record turns on, and a client screen that started naming
 * employees would be making a promise about who they can reach that the agreement does not make.
 */
export const PETROVIC_ACTOR = "Petrovic Systems";

/**
 * The signed-in tenant's own people, keyed by the Auth user id that appears in an actor column.
 *
 * The name is `public.contacts.full_name` and not anything out of `public.profiles`. That is forced
 * rather than chosen: `profiles_select_own` lets a client read exactly one profile row, their own, so
 * a colleague's profile is unreadable by construction. It is also the better source. `contacts` is
 * the register the tenant itself maintains, it sits one join from `memberships.contact_id`, and
 * `full_name` is `not null`, so a colleague who holds a membership is guaranteed to have a name.
 *
 * A person's own label still comes from their own profile. Their name is their own choice, and their
 * profile is the one row they can actually read.
 */
export type TenantRoster = ReadonlyMap<string, string>;

/**
 * Four answers about one row, where the client surfaces had three and the missing one was the
 * client's own colleagues.
 *
 * `actorSide` stops, on purpose, at "a known account that is not mine". Both client surfaces then
 * rendered every one of those as **Petrovic**. That is correct for a tenant with one person and
 * false for a tenant with two: the second colleague's comments read to the first as though the
 * supplier had written them, in the record of a paid engagement.
 *
 * The reason this is worth a query rather than a shrug: `public.contacts.is_authorised_approver`
 * marks the person whose word binds the client, and that person's approval inside a thread is the
 * evidence a change was agreed. A thread that cannot say which person wrote an entry cannot show
 * that the approver was the one who wrote it, and the flag then marks somebody the record never
 * names. The evidence collapses into the supplier apparently talking to itself.
 *
 * What separates the third answer from the fourth is membership, not equality. Nothing in the schema
 * deletes a `public.memberships` row; the admin gateway moves `status` instead. So an id that is
 * absent from the roster never belonged to this tenant, and Petrovic is the only side left for it.
 *
 * Membership `status` is deliberately not part of the question. "Who wrote this" is a fact about the
 * past, and a suspended colleague's old comment was still written by that colleague. The same
 * distinction is drawn on the admin side for `private.admin_users.active`, and for the same reason:
 * withdrawing somebody's access must not rewrite what they already said.
 */
export type ActorKind = "unknown" | "self" | "colleague" | "petrovic";

export interface ActorAttribution {
  kind: ActorKind;
  /** What the surface prints, and what a screen reader receives. Never a guess. */
  name: string;
  /** The avatar glyph beside it. */
  initial: string;
}

/**
 * `roster` is a resolved map, never `undefined`, and that is the whole contract of this function.
 *
 * A caller whose roster read has not finished has no attribution to render and must render its own
 * loading state instead. Passing an empty map while the read is in flight answers `Petrovic` for
 * every colleague, with complete confidence, and then corrects itself a moment later. A confident
 * wrong name that lasts one frame is the same fault as one that lasts forever; a reader has no way
 * to doubt either.
 */
export function attributeActor(
  actorUserId: string | null | undefined,
  currentUserId: string | null | undefined,
  ownName: string | null,
  roster: TenantRoster
): ActorAttribution {
  switch (actorSide(actorUserId, currentUserId)) {
    case "unknown":
      return { kind: "unknown", name: UNKNOWN_ACTOR, initial: UNKNOWN_ACTOR_INITIAL };
    case "self":
      return { kind: "self", name: ownName ?? "Du", initial: initials(ownName) };
    default: {
      // `actorSide` has already answered `counterpart`, so the id is a string.
      const colleague = roster.get(actorUserId as string);

      return colleague
        ? { kind: "colleague", name: colleague, initial: initials(colleague) }
        : { kind: "petrovic", name: PETROVIC_ACTOR, initial: initials(PETROVIC_ACTOR) };
    }
  }
}
